<template>
  <chartjs-wrapper
    ref="chartJsWrapper"
    class="white"
    chartId="realizedDemandBarChart"
    :chart-data="chartData"
    :type="'bar'"
    :options="chartOptions"
    :chart-height="10"
    :chart-width="'100%'"
    @click:element="clickedChartPoint"
  >
  </chartjs-wrapper>
</template>

<script>
import Vue from 'vue'
import DateRangeChartMixin from '@/components-internal/mixins/DateRangeChartMixin.js'
import _ from 'lodash'
import definitions from '../../../definitions'
import moment from 'moment'

export default Vue.extend({
  name: 'RealizedDemandChart',

  mixins: [DateRangeChartMixin],

  props: {
    selectedDate: {
      required: true,
      type: Date,
    },
    realizedDemand: {
      required: true,
      type: Object,
    },
    selectedDefinitions: {
      required: true,
      type: Array,
    },
  },

  computed: {
    chartData() {
      let data = {
        type: 'bar',
        labels: [],
        datasets: [],
      }

      // takes all dates in current range and adds each as a lable
      const startDate = moment(this.dateRange?.startDate || new Date())
      const endDate = moment(this.dateRange?.endDate || new Date())
      const daysDiff = endDate.diff(startDate, 'days')
      data.labels = _.times(daysDiff + 1, (daysOffset) =>
        startDate.clone().add(daysOffset, 'd').format('YYYY-MM-DD')
      )

      const demand = this.realizedDemand?.realizedDemand
      for (let prodDefId in demand) {
        const selection = _.find(
          this.selectedDefinitions,
          function (selection) {
            return selection.item.id === parseInt(prodDefId)
          }
        )

        let bar = {
          backgroundColor: selection?.color,
          data: [],
          /* global i18n */
          label: selection?.item?.getTranslatedName(),
        }

        // adds  for each lable a price (needed for chart-js to function)
        bar.data = data.labels.map((date) => {
          const dailyDemand = _.find(demand[prodDefId], (element) => {
            return element.validity_start_date === date
          })
          if (dailyDemand) {
            return parseInt(dailyDemand.tickets_nr)
          } else {
            return 0
          }
        })

        data.datasets.push(bar)
      }

      return data
    },
    chartOptions() {
      const mainOptions = {
        legend: {
          display: false,
        },
        layout: {
          padding: {
            right: 60,
          },
        },
        annotation: {
          annotations: [
            {
              drawTime: 'afterDatasetsDraw',
              id: 'todayLine',
              type: 'line',
              mode: 'vertical',
              scaleID: 'x-axis-0',
              value: new Date(),
              borderColor: definitions.colors.named.value,
              borderWidth: 1,
              borderDash: [5, 5],
              borderDashOffset: 0,
            },
            {
              drawTime: 'afterDatasetsDraw',
              id: 'selectionLine',
              type: 'line',
              mode: 'vertical',
              scaleID: 'x-axis-0',
              value: this.selectedDate,
              borderColor: definitions.colors.named.seasonality,
              borderWidth: 1,
              borderDash: [5, 5],
              borderDashOffset: 0,
            },
          ],
        },
        tooltips: {
          callbacks: {
            label: (tooltipItems, data) => {
              return (
                data.datasets[tooltipItems.datasetIndex].label +
                ': ' +
                tooltipItems.yLabel
              )
            },
          },
          caretPadding: 4,
          mode: 'nearest',
          backgroundColor: 'rgba(255,255,255,0.9)',
          intersect: true,
          xPadding: 10,
          yPadding: 10,
          titleMarginBottom: 12,
        },
        scales: {
          yAxes: [
            {
              scaleLabel: {
                labelString: this.$t('realizedDemandChart.yAxisLabel'),
                display: true,
              },
              gridLines: {
                display: true,
                color: '#EEE',
              },
              ticks: {
                suggestedMax: 4,
                maxTicksLimit: 4,
                suggestedMin: 4,
              },
              stacked: true,
              afterFit: function (scale) {
                scale.width = 60 // offset to align charts
              },
            },
          ],
          xAxes: [
            {
              gridLines: {
                display: false,
              },
              stacked: true,
            },
          ],
        },
      }
      return _.merge(this.baseChartOptions, mainOptions)
    },
  },

  watch: {
    // needed to update the vertical selcted date line
    chartOptions() {
      this.$refs.chartJsWrapper.reCreateChart()
    },
  },
})
</script>
